import { IFilter, IPaginationModel } from "@/api";

// API
export const API_URL = "/api/v1";

// Bunny
export const BUNNY_PULL_ZONE = import.meta.env.VITE_BUNNY_PULL_ZONE;

// HelpScout
export const HELPSCOUT_API_KEY = import.meta.env.VITE_HELPSCOUT_API_KEY;
export const HELP_CENTER_URL = "https://insurescope.helpscoutdocs.com/";

// Filtering
export const INITIAL_FILTER_MODEL: IFilter = {
    q: "",
    sort_by: "id",
    order: "desc",
};

// Pagination
export const DEFAULT_PAGINATION_MODEL: IPaginationModel = {
    page: 0,
    pageSize: 10,
};

// sidenav
export const DRAWER_WIDTH = 280;
export const COLLAPSED_DRAWER_WIDTH = 0;

// Top bar
export const TOP_BAR_HEIGHT = 64;

// Admin Banner
export const ADMIN_BANNER_HEIGHT = 55;

// scope sheet
export enum SheetSnapPoints {
    EXPANDED = 0.8,
    HALF = 0.5,
    DEFAULT = 210,
    MINIMAL = 50,
}

export const SNAP_POINTS = [
    SheetSnapPoints.MINIMAL,
    SheetSnapPoints.DEFAULT,
    SheetSnapPoints.HALF,
    SheetSnapPoints.EXPANDED,
];

// scope toolbar
export const SCOPE_TOOLBAR_DESKTOP_HEIGHT = "calc(100vh - 64px - 77px)";
export const SCOPE_TOOLBAR_MOBILE_HEIGHT = "calc(100dvh - 64px - 116px)";

// pdf viewer
export const HIGHLIGHT_COLORS = [
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FFFF00",
    "#FF00FF",
    "#00FFFF",
    "#800000",
    "#808000",
    "#008000",
    "#800080",
    "#008080",
    "#000080",
    "#FF4500",
    "#DA70D6",
    "#7FFF00",
    "#D2691E",
    "#6495ED",
    "#DC143C",
    "#00CED1",
    "#B8860B",
    "#006400",
    "#8B008B",
    "#556B2F",
    "#FF8C00",
    "#9932CC",
    "#E9967A",
    "#8FBC8F",
    "#483D8B",
    "#2F4F4F",
    "#00BFFF",
    "#FFD700",
    "#ADFF2F",
    "#FF69B4",
    "#CD5C5C",
    "#4B0082",
    "#F08080",
    "#90EE90",
    "#FFB6C1",
    "#FFA07A",
    "#20B2AA",
    "#87CEFA",
    "#778899",
    "#B0C4DE",
    "#32CD32",
    "#66CDAA",
    "#BA55D3",
    "#9370DB",
    "#3CB371",
    "#7B68EE",
    "#00FA9A",
];

/**
 * Get a consistent highlight color for a clause based on its ID.
 * This ensures the same clause always gets the same color in both
 * the PDF viewer and the clause list UI.
 */
export const getClauseHighlightColor = (clauseId: number): string => {
    return HIGHLIGHT_COLORS[clauseId % HIGHLIGHT_COLORS.length];
};

// Clause accuracy
export const ACCURACY_COLORS = [
    "rgba(132, 242, 22, 0.2)",
    "rgba(255, 214, 0, 0.3)",
    "rgba(242, 22, 22, 0.2)",
];

// File uploading
export enum FileError {
    InvalidFileType = "Invalid File Type",
    Required = "Please Attach a File",
    FileTooLarge = "File is too large",
    UploadFailed = "Upload Failed",
}

const fileSizeConstant = 200;
export const MAX_FILE_SIZE_MB = fileSizeConstant;
export const MAX_FILE_SIZE_BYTES = fileSizeConstant * 1024 * 1024; // 200MB
export const MAX_FILE_SIZE_DEC = fileSizeConstant * 1000000;

// react-dropzone `accept` maps (mime -> extensions), shared so every upload
// surface stays byte-for-byte consistent. PDF-only is the safe default for
// contract flows; the library/chat upload surfaces also take spreadsheets.
export const PDF_ACCEPT = { "application/pdf": [".pdf"] };
export const PDF_SPREADSHEET_ACCEPT = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
    ],
    "text/csv": [".csv"],
};

// Default Query Filters
export const CLAUSE_QUERY_FILTER: IFilter = {
    sort_by: "quick",
    order: "desc",
};

export const ORGANIZATION_QUERY_FILTER: IFilter = {
    sort_by: "name",
    order: "desc",
};

export const CLAUSE_CHECKLIST_QUERY_FILTER: IFilter = {
    sort_by: "name",
    order: "desc",
};

export const USER_QUERY_FILTER: IFilter = {
    order: "desc",
};

export const CLAUSE_TYPE_QUERY_FILTER: IFilter = {
    sort_by: "name",
    order: "asc",
};

export const POLICY_TYPE_QUERY_FILTER: IFilter = {
    sort_by: "name",
    order: "asc",
};

export const TOPIC_QUERY_FILTER: IFilter = {
    sort_by: "name",
    order: "asc",
};

// Task refetch interval
export const TASK_REFETCH_INTERVAL = 1000 * 10; // every 10 seconds

// Scroll threshold for hiding/showing the top bar
export const SCROLL_THRESHOLD: number = 50;

// Auth Layout
export const AUTH_TOP_BAR_HEIGHT = 64;

// Current Year
export const CURRENT_YEAR = new Date().getFullYear();

export const DEFAULT_GUIDANCE_LABEL = "Default Guidance from InsureScope";

export const HOME_ORG_PATH = "/organizations/home";

export const POLLY_DISCLAIMER_COPY =
    "Polly is AI and can make mistakes. Please verify responses.";
